import { Component } from '@theme/component';

/** Delay before a hover-opened dropdown closes, so the pointer can cross the gap into it. */
const CLOSE_DELAY = 140;

/** Minimum distance between a dropdown and the viewport edge. */
const VIEWPORT_GUTTER = 12;

/** Share of the frame kept as padding on each side when zoomed in on a part. */
const FOCUS_PADDING = 0.1;

/**
 * Pans and scales a vehicle illustration to the part a shopper picks from the part row,
 * and opens that part's product dropdown beneath it. It is a 2D camera over one image,
 * not a 3D model.
 *
 * Mirrors the reference storefront: on a fine pointer, hovering a part zooms in and opens
 * its dropdown, and leaving both the part and its dropdown closes it and zooms back out.
 * Clicking or tapping elsewhere and Escape close it.
 *
 * A part is a link to its collection page when it has one, otherwise a button. Mouse
 * clicks and keyboard activation follow a part link; on touch, the first tap opens the
 * dropdown and a second tap follows the link. Keyboard focus on a part link opens its
 * dropdown. Part buttons toggle the dropdown on taps and keyboard activation.
 *
 * Coordinates are fractions of the image box: data-focus-x / data-focus-y on each part
 * button, plus data-view-top / data-view-bottom on the host, which bound the vehicle
 * inside the artwork so the overview frames the vehicle rather than the image.
 *
 * Dispatches a bubbling `vehicle-explorer:select` event with `{ partId, title }`
 * (`partId` is null when the full view is restored).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - Clipping box the camera is measured against.
 * @property {HTMLElement} world - Element the camera transform is applied to.
 * @property {HTMLElement} [dock] - Positioning context for the dropdowns.
 * @property {HTMLButtonElement} [resetButton] - Returns to the full vehicle view.
 * @property {HTMLElement[]} [parts] - Part row links or buttons; the source of part data.
 * @property {HTMLElement[]} [menus] - One product dropdown per part, matched by data-part.
 * @property {HTMLElement} liveRegion - Announces selection changes.
 *
 * @extends {Component<Refs>}
 */
class VehicleExplorer extends Component {
  requiredRefs = ['viewport', 'world', 'liveRegion'];

  /** Part the camera is zoomed into. @type {HTMLElement | null} */
  #activePart = null;

  /** Part whose dropdown is open. @type {HTMLElement | null} */
  #openPart = null;

  /** @type {number | undefined} */
  #closeTimer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** Listeners on the host, document and window. @type {AbortController | undefined} */
  #abortController;

  /** Hover listeners on the current part and menu nodes. @type {AbortController | undefined} */
  #hoverController;

  /** @type {number | undefined} */
  #frame;

  /** True while focus is moved back to a part by script, so the move doesn't reopen its dropdown. */
  #restoringFocus = false;

  #finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    window.addEventListener('resize', this.#placeAllMenus, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateCamera();
      this.#placeAllMenus();
    });
    this.#observe();
    this.#bindHover();

    // Place the camera without animating, then enable the transition for later moves.
    const { world } = this.refs;
    world.style.transition = 'none';
    this.#updateCamera();
    this.#placeAllMenus();
    this.#frame = requestAnimationFrame(() => {
      this.#frame = requestAnimationFrame(() => {
        world.style.transition = '';
      });
    });
  }

  updatedCallback() {
    super.updatedCallback();

    this.#activePart = this.#findPart(this.#activePart?.dataset.part);
    this.#openPart = this.#findPart(this.#openPart?.dataset.part);
    this.#resizeObserver?.disconnect();
    this.#observe();
    this.#bindHover();
    this.#syncState();
    this.#updateCamera();
    this.#placeAllMenus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#hoverController?.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#closeTimer);
    if (this.#frame !== undefined) cancelAnimationFrame(this.#frame);
  }

  /**
   * Handles a click, tap or keyboard activation on a part. A part link navigates, except
   * that a tap opens a closed dropdown first. On a part button, a mouse click on a fine
   * pointer keeps the dropdown open (hover already opened it); taps and keyboard
   * activation toggle it.
   * @param {MouseEvent} event
   */
  togglePart(event) {
    const target = event.target;
    if (!(target instanceof Node)) return;

    const part = this.refs.parts?.find((candidate) => candidate.contains(target));
    if (!part) return;

    const fromKeyboard = event.detail === 0;

    if (part instanceof HTMLAnchorElement) {
      // In the theme editor a click previews the part instead of leaving the page being edited.
      const inEditor = Boolean(window.Shopify?.designMode);
      if (!inEditor && (this.#finePointer.matches || fromKeyboard || this.#openPart === part)) return;

      event.preventDefault();
      this.#openGroup(part);
      return;
    }

    if (this.#finePointer.matches && !fromKeyboard) {
      this.#cancelClose();
      this.#openGroup(part);
    } else if (this.#openPart === part) {
      this.#closeGroup();
    } else {
      this.#openGroup(part);
    }
  }

  /**
   * Returns to the full vehicle view.
   */
  reset() {
    const previous = this.#activePart;

    this.#closeGroup(true);
    this.#select(null);
    if (previous) this.#restoreFocus(previous);
  }

  /**
   * @param {HTMLElement} part
   */
  #restoreFocus(part) {
    this.#restoringFocus = true;
    part.focus({ preventScroll: true });
    this.#restoringFocus = false;
  }

  /**
   * @param {HTMLElement} part
   */
  #openGroup(part) {
    if (this.#openPart === part) return;

    this.#closeGroup(true);
    this.#openPart = part;
    this.#placeMenu(part);
    this.#syncState();

    if (this.#activePart !== part) this.#select(part);
  }

  /**
   * @param {boolean} [keepZoom] - Leave the camera where it is instead of returning to the full view.
   */
  #closeGroup(keepZoom = false) {
    const part = this.#openPart;
    if (!part) return;

    this.#openPart = null;
    this.#syncState();

    if (!keepZoom && this.#activePart === part) this.#select(null);
  }

  #scheduleClose = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.#closeGroup(), CLOSE_DELAY);
  };

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
  };

  /**
   * @param {HTMLElement | null} part
   */
  #select(part) {
    if (part === this.#activePart) return;

    this.#activePart = part;
    this.#syncState();
    this.#updateCamera();

    this.refs.liveRegion.textContent = part?.dataset.announcement ?? this.dataset.resetAnnouncement ?? '';

    this.dispatchEvent(
      new CustomEvent('vehicle-explorer:select', {
        bubbles: true,
        detail: { partId: part?.dataset.part ?? null, title: part?.dataset.title ?? null },
      })
    );
  }

  /**
   * @param {string | undefined} partId
   * @returns {HTMLElement | null}
   */
  #findPart(partId) {
    if (!partId) return null;
    return this.refs.parts?.find((part) => part.dataset.part === partId) ?? null;
  }

  /**
   * @param {string | undefined} partId
   * @returns {HTMLElement | null}
   */
  #findMenu(partId) {
    if (!partId) return null;
    return this.refs.menus?.find((menu) => menu.dataset.part === partId) ?? null;
  }

  #syncState() {
    const active = this.#activePart;
    const open = this.#openPart;

    this.refs.world.classList.toggle('is-focused', Boolean(active));
    if (this.refs.resetButton) this.refs.resetButton.hidden = !active;

    for (const part of this.refs.parts ?? []) {
      part.classList.toggle('is-active', part === active);
      part.setAttribute('aria-expanded', String(part === open));
    }

    for (const menu of this.refs.menus ?? []) {
      menu.classList.toggle('is-open', menu.dataset.part === open?.dataset.part);
    }
  }

  #observe() {
    this.#resizeObserver?.observe(this.refs.viewport);
    if (this.refs.dock) this.#resizeObserver?.observe(this.refs.dock);
  }

  /**
   * Hover opens and closes dropdowns only on a fine pointer; touch input goes through togglePart.
   * Keyboard focus on a part link opens its dropdown, since activating the link navigates.
   */
  #bindHover() {
    this.#hoverController?.abort();
    this.#hoverController = new AbortController();
    const { signal } = this.#hoverController;

    for (const part of this.refs.parts ?? []) {
      part.addEventListener(
        'mouseenter',
        () => {
          if (!this.#finePointer.matches) return;
          this.#cancelClose();
          this.#openGroup(part);
        },
        { signal }
      );
      part.addEventListener('mouseleave', this.#onPointerLeave, { signal });

      if (part instanceof HTMLAnchorElement) {
        part.addEventListener(
          'focus',
          () => {
            if (this.#restoringFocus || !part.matches(':focus-visible')) return;
            this.#cancelClose();
            this.#openGroup(part);
          },
          { signal }
        );
      }
    }

    for (const menu of this.refs.menus ?? []) {
      menu.addEventListener('mouseenter', this.#cancelClose, { signal });
      menu.addEventListener('mouseleave', this.#onPointerLeave, { signal });
    }

    // On phones the part row scrolls sideways; keep an open dropdown under its part.
    this.refs.dock?.addEventListener('scroll', this.#placeAllMenus, { signal, passive: true });
  }

  #onPointerLeave = () => {
    if (this.#finePointer.matches) this.#scheduleClose();
  };

  /**
   * Places a dropdown below the part row, aligned with its part and nudged to stay inside
   * the viewport. Offsets are measured from the dropdown's offset parent: the part row,
   * or on phones (where the row scrolls) the visual around it.
   * @param {HTMLElement} part
   */
  #placeMenu(part) {
    const { dock } = this.refs;
    const menu = this.#findMenu(part.dataset.part);
    if (!dock || !menu) return;

    const container = menu.offsetParent instanceof HTMLElement ? menu.offsetParent : dock;
    const containerRect = container.getBoundingClientRect();
    const dockRect = dock.getBoundingClientRect();
    const partRect = part.getBoundingClientRect();
    const width = menu.offsetWidth;

    let viewportLeft = partRect.left;
    if (viewportLeft + width > window.innerWidth - VIEWPORT_GUTTER) {
      viewportLeft = window.innerWidth - VIEWPORT_GUTTER - width;
    }
    if (viewportLeft < VIEWPORT_GUTTER) viewportLeft = VIEWPORT_GUTTER;

    menu.style.left = `${viewportLeft - containerRect.left}px`;
    menu.style.top = `${dockRect.bottom - containerRect.top}px`;
  }

  #placeAllMenus = () => {
    for (const part of this.refs.parts ?? []) this.#placeMenu(part);
  };

  #updateCamera() {
    const { viewport, world } = this.refs;
    const width = viewport.clientWidth;
    const height = viewport.clientHeight;
    const aspect = parseFloat(this.dataset.aspect ?? '') || 16 / 9;
    const imageHeight = width / aspect;
    if (!width || !imageHeight) return;

    const part = this.#activePart;

    if (!part) {
      const top = parseFloat(this.dataset.viewTop ?? '0');
      const bottom = parseFloat(this.dataset.viewBottom ?? '1');
      const left = parseFloat(this.dataset.viewLeft ?? '0');
      const right = parseFloat(this.dataset.viewRight ?? '1');
      const fit = Math.min(1, height / (imageHeight * Math.max(bottom - top, 0.01)));
      // Center the framed content box, not the image bounds, so off-center artwork sits balanced.
      const x = width / 2 - ((left + right) / 2) * width * fit;
      const y = height / 2 - ((top + bottom) / 2) * imageHeight * fit;

      world.style.transform = `translate(${x}px, ${y}px) scale(${fit})`;
      return;
    }

    const focusX = parseFloat(part.dataset.focusX ?? '0.5');
    const focusY = parseFloat(part.dataset.focusY ?? '0.5');
    const zoom = parseFloat(part.dataset.zoom ?? '1') || 1;

    // The part's zoom window is fitted inside the padded frame, then the pan is clamped so the
    // frame never runs past the image edges. Same rule for every part.
    const scale = zoom * (1 - 2 * FOCUS_PADDING);
    const scaledWidth = width * scale;
    const scaledHeight = imageHeight * scale;
    const x = clamp(width / 2 - focusX * scaledWidth, width - scaledWidth, 0);
    const y = clamp(height / 2 - focusY * scaledHeight, height - scaledHeight, 0);

    world.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  }

  /**
   * Closes the dropdown on any click outside the part buttons and dropdowns.
   * Following a product link keeps the zoom while the page navigates.
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (this.refs.parts?.some((part) => part.contains(target))) return;

    if (target.closest('a[href]') && this.refs.menus?.some((menu) => menu.contains(target))) {
      this.#closeGroup(true);
      return;
    }

    if (this.refs.menus?.some((menu) => menu.contains(target))) return;

    this.#closeGroup();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (document.querySelector('dialog[open]')) return;

    const open = this.#openPart;
    const focusInside = open && this.#findMenu(open.dataset.part)?.contains(document.activeElement);

    if (open) {
      this.#closeGroup();
      if (focusInside) this.#restoreFocus(open);
    }

    if (this.#activePart) this.#select(null);
  };

  /**
   * Closes a dropdown once keyboard focus leaves both its part button and the dropdown.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const open = this.#openPart;
    if (!open) return;

    const next = event.relatedTarget;
    if (!(next instanceof Node)) return;

    const menu = this.#findMenu(open.dataset.part);
    if (open.contains(next) || menu?.contains(next)) return;

    this.#closeGroup();
  };

  /**
   * Opens a part while it is selected in the theme editor, so its zoom and products can be previewed.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const part = this.#findPart(event.target.dataset.part);
    if (part) this.#openGroup(part);
  };

  #onBlockDeselect = () => {
    this.#closeGroup();
  };
}

if (!customElements.get('vehicle-explorer')) {
  customElements.define('vehicle-explorer', VehicleExplorer);
}

/**
 * Clamps a camera offset between `min` and `max`, centring it when the image is smaller than the frame.
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  if (min > max) return (min + max) / 2;
  return Math.min(max, Math.max(min, value));
}
